<template>
  <table class="access-table">
    <thead>
      <tr>
        <th>Email</th>
        <th>Username</th>
        <th>Date</th>
        <th>Permissions</th>
        <th class="access-table__add-col">
          <IconButton
            :icon="'person_add'"
            aria-label="Add User"
            title="Add New User"
            :width="22"
            :height="22"
            :showHover="true"
            @click="showAddUserForm = !showAddUserForm"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="showAddUserForm">
        <td>
          <input
            v-model="newUserEmail"
            type="email"
            class="email-input"
            placeholder="Enter email address"
            @keyup.enter="addAccess"
          >
        </td>
        <td />
        <td />
        <td>
          <div class="permission-select-wrapper">
            <select v-model="newUserPermission" class="permission-select">
              <option value="read">Read only</option>
              <option value="full">Full</option>
            </select>
            <SVGIcon
              :iconName="'chevron_left'"
              :width="'14px'"
              :height="'14px'"
              class="permission-select-chevron"
            />
          </div>
        </td>
        <td class="access-table__add-col">
          <div class="d-flex justify-content-end">
            <IconButton
              :icon="'check'"
              aria-label="Add User"
              title="Add User"
              :width="22"
              :height="22"
              :disabled="!newUserEmail.trim()"
              :showHover="true"
              @click="addAccess"
            />
            <IconButton
              :icon="'close'"
              aria-label="Cancel"
              title="Cancel"
              :width="22"
              :height="22"
              :showHover="true"
              @click="cancelAddUser"
            />
          </div>
        </td>
      </tr>
      <tr
        v-for="(user, index) in projectAccessList"
        :key="index"
      >
          <td>{{ user.email }}</td>
          <td>
            <div class="users__container">
              <div class="users__icon">
                <UserIcon
                  v-if="user.username"
                  :user="user"
                  :size="'32px'"
                />
              </div>
              <span class="users__username d-flex align-items-center">
                {{ user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username }}
                <SVGIcon
                  v-if="org && user.organization_id !== org.Id"
                  v-tooltip="'External users are given access to this project through direct sharing.'"
                  :iconName="'web'"
                  :width="'18px'"
                  :height="'18px'"
                  class="ms-2"
                />
              </span>
            </div>
          </td>
          <td>{{ formatDate(user.created) }}</td>
          <td>
            <template v-if="(user.username === currentUser.username || (modalProject && modalProject.username === user.username))">
              <div class="users__role">
                <template v-if="modalProject.username === user.username">
                  <SVGIcon
                    :iconName="'key'"
                    :width="'18px'"
                    :height="'18px'"
                    class="role-icon"
                  />
                  <span>Owner</span>
                </template>
                <template v-else>
                  <span>{{ getPermissionLabel(user) }}</span>
                </template>
              </div>
            </template>
            <template v-else-if="editingUsername === user.username">
              <div class="permission-select-wrapper">
                <select v-model="editPermission" class="permission-select">
                  <option value="read">Read only</option>
                  <option value="full">Full</option>
                </select>
                <SVGIcon
                  :iconName="'chevron_left'"
                  :width="'14px'"
                  :height="'14px'"
                  class="permission-select-chevron"
                />
              </div>
            </template>
            <template v-else>
              <div class="users__role">
                <span>{{ getPermissionLabel(user) }}</span>
              </div>
            </template>
          </td>
          <td class="access-table__add-col">
            <template v-if="!(user.username === currentUser.username || (modalProject && modalProject.username === user.username))">
              <div class="d-flex justify-content-end">
                <template v-if="editingUsername === user.username">
                  <IconButton
                    :icon="'check'"
                    aria-label="Save"
                    title="Save"
                    :width="22"
                    :height="22"
                    :showHover="true"
                    @click="saveEditUser(user)"
                  />
                  <IconButton
                    :icon="'close'"
                    aria-label="Cancel"
                    title="Cancel"
                    :width="22"
                    :height="22"
                    :showHover="true"
                    @click="cancelEditUser"
                  />
                </template>
                <template v-else>
                  <IconButton
                    :icon="'edit'"
                    aria-label="Edit"
                    title="Edit Permissions"
                    :width="22"
                    :height="22"
                    :showHover="true"
                    @click="startEditUser(user)"
                  />
                  <IconButton
                    :icon="'trash'"
                    aria-label="Remove Access"
                    title="Remove Access"
                    :width="22"
                    :height="22"
                    :showHover="true"
                    @click="handleDeleteUserAccess(user)"
                  />
                </template>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="confirmButtonClass"
    :buttonText="confirmButtonText"
    @confirmed="confirmFunction"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import UserIcon from '@/components/UserIcon.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'UserAccessList',
  components: {
    SVGIcon,
    IconButton,
    UserIcon,
    ConfirmModal,
  },
  props: {
    modalProject: {
      type: Object,
      default: () => {},
    },
    shareMode: {
      type: String,
      default: '',
    },
  },
  emits: ['refresh-projects'],
  data() {
    return {
      dsConn: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      projectAccessList: [],
      newUserEmail: '',
      newUserPermission: 'read',
      editingUsername: null,
      editPermission: 'read',
      org: null,
      showAddUserForm: false,
      confirmMessage: null,
      confirmMessageHeader: null,
      confirmButtonText: '',
      confirmButtonClass: undefined,
      confirmFunction: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user.user;
    },
  },
  watch: {
    modalProject: {
      immediate: true,
      handler() {
        if (this.modalProject && this.modalProject.id) {
          this.getOrg(this.modalProject.organization_id);
          this.refreshAccessList();
        }
      },
    },
  },
  methods: {
    async getOrg(organization_id) {
      await this.dsConn.getOrg({ organization_id })
        .then((data) => {
          if (data.result) {
            this.org = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error retrieving organization: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((e) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error retrieving organization: ${e}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    sortByName(a, b) {
      const lastA = a.last_name.toLowerCase();
      const lastB = b.last_name.toLowerCase();
      const firstA = a.first_name.toLowerCase();
      const firstB = b.first_name.toLowerCase();

      if (firstA < firstB) return -1;
      if (firstA > firstB) return 1;

      if (lastA < lastB) return -1;
      if (lastA > lastB) return 1;

      return 0;
    },
    formatDate(str) {
      if (!str) {
        return '';
      }
      const date = new Date(str);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleDateString('default', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getPermissionLabel(user) {
      const readFields = [user.project_read, user.dataset_read, user.trainer_read, user.annotator_read];
      const writeFields = [user.project_write, user.dataset_write, user.trainer_write, user.annotator_write];
      const allReadTrue = readFields.every(Boolean);
      const allWriteTrue = writeFields.every(Boolean);
      const allWriteFalse = writeFields.every((value) => !value);

      if (allReadTrue && allWriteTrue) {
        return 'Full';
      }
      if (allReadTrue && allWriteFalse) {
        return 'Read';
      }
      return 'Custom';
    },
    async refreshAccessList() {
      if (this.modalProject && this.shareMode !== 'public') {
        await this.dsConn.getProjectAccessList({ project_id: this.modalProject.id })
          .then((data) => {
            if (data.result) {
              const temp = data.result;
              temp.sort(this.sortByName);

              this.projectAccessList = temp.sort(this.sortByName);
            } else {
              this.projectAccessList = [];
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error retrieving access list: ${e}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          });
      }
    },
    async updateProjectAccess(username, mode) {
      const list = [
        {
          username,
          project_read: true,
          project_write: false,
          dataset_read: true,
          dataset_write: false,
          trainer_read: true,
          trainer_write: false,
          annotator_read: true,
          annotator_write: false,
        },
      ];
      if (mode === 'editor') {
        list[0].project_write = true;
        list[0].dataset_write = true;
        list[0].trainer_write = true;
        list[0].annotator_write = true;
      }
      await this.dsConn.updateProjectAccess({
        project_id: this.modalProject.id,
        project_access_list: list,
      })
        .then((data) => {
          if (!data.result || data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error updating access: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          } else {
            this.refreshAccessList();
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error updating access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    startEditUser(user) {
      this.editingUsername = user.username;
      this.editPermission = user.project_write ? 'full' : 'read';
    },
    cancelEditUser() {
      this.editingUsername = null;
    },
    async saveEditUser(user) {
      await this.updateProjectAccess(user.username, this.editPermission === 'full' ? 'editor' : 'viewer');
      this.editingUsername = null;
    },
    handleDeleteUserAccess(user) {
      this.confirmMessage = `Are you sure you want to remove access for user ${user.first_name} ${user.last_name}?`;
      this.confirmMessageHeader = 'Remove Access';
      this.confirmButtonText = 'Remove';
      this.confirmButtonClass = "button-delete";
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => { this.deleteProjectAccess(user.username); };
    },
    async deleteProjectAccess(username) {
      await this.dsConn.deleteProjectAccess({
        project_id: this.modalProject.id,
        project_access_usernames: [username],
      })
        .then((data) => {
          if (!data.result || data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error removing access: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          } else {
            this.refreshAccessList();
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error removing access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async addAccess() {
      const email = this.newUserEmail.trim();
      if (!email) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: "Please enter an e-mail address.",
          type: "error",
          position: "top",
          duration: 5000,
        });
        return;
      }
      const permissions = {};
      if (this.newUserPermission === 'full') {
        permissions.project_read = true;
        permissions.project_write = true;
        permissions.dataset_read = true;
        permissions.dataset_write = true;
        permissions.trainer_read = true;
        permissions.trainer_write = true;
        permissions.annotator_read = true;
        permissions.annotator_write = true;
      } else if (this.newUserPermission === 'read') {
        permissions.project_read = true;
        permissions.project_write = false;
        permissions.dataset_read = true;
        permissions.dataset_write = false;
        permissions.trainer_read = true;
        permissions.trainer_write = false;
        permissions.annotator_read = true;
        permissions.annotator_write = false;
      } else {
        return;
      }

      await this.dsConn.addProjectAccess({
        project_id: this.modalProject.id,
        usernames: [email],
        permissions,
      })
        .then((data) => {
          if (!data.result || data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error adding access: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          } else {
            this.newUserEmail = '';
            this.newUserPermission = 'read';
            this.showAddUserForm = false;
            this.refreshAccessList();
            this.$emit('refresh-projects');
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error adding access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    cancelAddUser() {
      this.newUserEmail = '';
      this.newUserPermission = 'read';
      this.showAddUserForm = false;
    },
    reset() {
      this.projectAccessList = [];
      this.newUserEmail = '';
      this.newUserPermission = 'read';
      this.showAddUserForm = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.access-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid var(--color-gray-300, #d0d0d0);
  border-radius: 8px;
  background: #ffffff;

  th, td {
    text-align: left;
    padding: 8px 10px;
    font-size: 0.9rem;
    vertical-align: middle;
  }

  .email-input {
    width: 100%;
    height: 30px;
    box-sizing: border-box;
    border: 1px solid var(--color-gray-300, #d0d0d0);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 0.9rem;
  }

  .permission-select-wrapper {
    position: relative;
    display: inline-block;
  }

  .permission-select {
    height: 30px;
    box-sizing: border-box;
    border: 1px solid var(--color-gray-300, #d0d0d0);
    border-radius: 4px;
    padding: 4px 24px 4px 8px;
    font-size: 0.9rem;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }

  .permission-select-chevron {
    position: absolute;
    top: 50%;
    right: 6px;
    transform: translateY(-50%) rotate(-90deg);
    pointer-events: none;
  }

  th {
    font-weight: 600;
    border-bottom: 1px solid var(--color-gray-300, #d0d0d0);
  }

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid var(--color-gray-200, #e5e5e5);
  }

  &__add-col {
    text-align: right;
    width: 40px;
  }
}

.users__container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.users__icon {
  padding-right: 16px;
}

.users__username {
  font-weight: 600;
  font-size: 16px;
}

.users__subtitle {
  font-weight: 400;
  font-size: 12px;
}

.users__role {
  border-radius: 32px;
  height: 34px;
  width: fit-content;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .role-icon {
    margin-right: 5px;
  }
}
</style>
